import { Injectable } from '@angular/core'
import { ServicesSubscriberEvent } from '@app/models/others.model'

import { Subject, tap } from 'rxjs'
import { OPEN_ONCE, CLEAN_RELAY, SET_STATION_RESTRICTED_NOW } from '@app/query/station.query'
import { ApiService } from '@app/core/graphql/services/api.service'
import { MessageService } from 'primeng/api'
import { TranslateMessagesService } from './translate-messages.service'

interface StationLike {
  id: string | number
  client_id?: string | number
}

interface StationMutationResponse {
  success?: boolean
  data?: unknown
  keep_open?: boolean
}

@Injectable()
export class StationService {
  subscriber: Subject<ServicesSubscriberEvent> = new Subject<ServicesSubscriberEvent>()
  id: string = 'STATION'
  sysMessage: ReturnType<TranslateMessagesService['create']>
  constructor(
    private API: ApiService,
    private translateMessagesService: TranslateMessagesService,
    private messageService: MessageService,
  ) {
    this.sysMessage = this.translateMessagesService.create(this.id)
  }

  /**
   * Open oncestation or entrance=>stations
   * @param station_or_entrance Station or entrance data object
   * @param isEntrance Is entrance?
   * @param silence
   * @returns
   */
  openOnce(station_or_entrance: StationLike, isEntrance?: boolean | null, silence: boolean = false) {
    return this.API.privateMutation<StationMutationResponse>(OPEN_ONCE, (data: Record<string, unknown>) => {
      data['station_id'] = !isEntrance ? parseInt(station_or_entrance.id as string) : null
      data['entrance_id'] = isEntrance ? parseInt(station_or_entrance.id as string) : null
      return { data }
    }).pipe(
      tap((data) => {
        if (silence) return
        if (data.success) {
          this.messageService.add({
            severity: 'success',
            icon: 'pi pi-check-circle color-success',
            ...this.sysMessage.message('OPEN_STATION_ONCE_SUCCESS', station_or_entrance),
          })
          this.subscriber.next({
            type: `STATION_OPEN_ONCE`,
            data: data.data,
          })
        }
      }),
    )
  }

  /**
   * Set station restricted_now flag (immediate lock/unlock)
   */
  setRestrictedNow(station: StationLike, value: boolean, silence: boolean = false) {
    return this.API.privateMutation<StationMutationResponse>(SET_STATION_RESTRICTED_NOW, (vars: Record<string, unknown>) => ({
      station_id: parseInt(station.id as string),
      client_id: vars['client_id'] || parseInt(station.client_id as string),
      value,
    })).pipe(
      tap((data) => {
        if (silence) return
        if (data?.success) {
          this.messageService.add({
            severity: value ? 'warn' : 'success',
            icon: value ? 'pi pi-lock' : 'pi pi-lock-open',
            ...this.sysMessage.message(value ? 'STATION_LOCKED' : 'STATION_UNLOCKED'),
          })
          this.subscriber.next({ type: 'STATION_RESTRICTED_NOW', data: data.data })
        }
      }),
    )
  }

  /**
   * Clean Bluetooth relay for a station
   * @param station Station data object
   * @param silence
   * @returns
   */
  cleanRelay(station: StationLike, silence: boolean = false) {
    return this.API.privateMutation<StationMutationResponse>(CLEAN_RELAY, (data: Record<string, unknown>) => {
      data['station_id'] = parseInt(station.id as string)
      return { data }
    }).pipe(
      tap((data) => {
        if (silence) return
        if (data.success) {
          this.messageService.add({
            severity: 'success',
            icon: 'pi pi-check-circle color-success',
            summary: 'Success',
            detail: 'Bluetooth relay cleaned successfully',
          })
          this.subscriber.next({
            type: `STATION_CLEAN_RELAY`,
            data: data.data,
          })
        }
      }),
    )
  }
}
